// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import {
  CASE_ENTITY_KIND_EXCHANGE,
  CASE_ENTITY_KIND_NON_EXCHANGE,
  CASE_ENTITY_KIND_UNKNOWN,
  CASE_KNOWN_ENTITY_CASING_REVIEW_FAMILY,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_REVIEWED
} from './CaseBuddyAppConstants.js';
import { buildEffectiveEntityCurators, formatCaseEntityKindLabel, normalizeCaseEntityTerm } from './CaseBuddyEntityCurators.js';
import { abbreviateCaseBuddyHash, caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export function normalizeKnownEntityCasingReviewStatus(status = '') {
  const normalized = normalizeCell(status).toLowerCase();
  if (normalized === CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED) return CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED;
  if (normalized === CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED) return CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED;
  return CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_REVIEWED;
}

export function formatKnownEntityCasingReviewStatus(status = '') {
  const normalized = normalizeKnownEntityCasingReviewStatus(status);
  if (normalized === CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED) return 'Dismissed';
  if (normalized === CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED) return 'Applied';
  return 'Reviewed';
}

export function splitKnownEntityCasingWords(value = '') {
  const normalized = normalizeCaseEntityTerm(value);
  return normalized ? normalized.split(' ').filter(Boolean) : [];
}

export function buildKnownEntityCasingPatterns(effective = {}) {
  const items = Array.isArray(effective?.all) ? effective.all : [];
  const suppressed = new Set((Array.isArray(effective?.suppressedTerms) ? effective.suppressedTerms : []).map(term => normalizeCaseEntityTerm(term)).filter(Boolean));
  const seen = new Set();
  const patterns = [];

  items.forEach((item) => {
    const name = normalizeCell(item?.name);
    const kind = normalizeCell(item?.kind) || CASE_ENTITY_KIND_UNKNOWN;
    const normalizedName = normalizeCaseEntityTerm(name);
    const words = splitKnownEntityCasingWords(name);
    if (!name || !normalizedName || words.length === 0 || suppressed.has(normalizedName)) return;

    const key = `${kind}|${normalizedName}`;
    if (seen.has(key)) return;
    seen.add(key);
    patterns.push({
      id: `known-casing-pattern-${caseBuddyHashString(key)}`,
      name,
      canonicalName: name,
      presentedName: name,
      kind,
      kindLabel: formatCaseEntityKindLabel(kind),
      source: item?.source || '',
      sourceLabel: item?.sourceLabel || '',
      words
    });
  });

  return patterns.sort((left, right) => {
    const wordDiff = right.words.length - left.words.length;
    if (wordDiff !== 0) return wordDiff;
    const lengthDiff = right.name.length - left.name.length;
    if (lengthDiff !== 0) return lengthDiff;
    return left.name.localeCompare(right.name);
  });
}

export function tokenizeKnownEntityCasingNotes(notes = '') {
  const source = String(notes || '');
  const tokens = [];
  let index = 0;

  while (index < source.length) {
    if (!/[A-Za-z0-9]/.test(source[index])) {
      index += 1;
      continue;
    }

    const start = index;
    while (index < source.length && /[A-Za-z0-9]/.test(source[index])) {
      index += 1;
    }

    const endExclusive = index;
    const rawText = source.slice(start, endExclusive);
    tokens.push({
      start,
      endExclusive,
      rawText,
      normalizedWord: rawText.toLowerCase()
    });
  }

  return tokens;
}

export function knownEntityCasingPatternMatchesAt(tokens = [], startIndex = 0, pattern = {}) {
  if (!Array.isArray(tokens) || !Array.isArray(pattern.words) || pattern.words.length === 0) return false;
  if (startIndex < 0 || startIndex + pattern.words.length > tokens.length) return false;

  for (let offset = 0; offset < pattern.words.length; offset += 1) {
    if (tokens[startIndex + offset]?.normalizedWord !== pattern.words[offset]) return false;
  }

  return true;
}

export function buildKnownEntityCasingExcerpt(notes = '', start = 0, endExclusive = 0, context = 42) {
  const source = String(notes || '');
  if (!source) return '';
  const left = Math.max(0, start - context);
  const right = Math.min(source.length, endExclusive + context);
  return `${left > 0 ? '…' : ''}${source.slice(left, right)}${right < source.length ? '…' : ''}`;
}

export function buildKnownEntityCasingReviewBasisHash(candidateSeed = {}) {
  const payload = {
    rowIndex: candidateSeed.rowIndex,
    hash: candidateSeed.hash || '',
    canonicalName: candidateSeed.canonicalName || '',
    kind: candidateSeed.kind || '',
    currentNotes: candidateSeed.currentNotes || '',
    proposedNotes: candidateSeed.proposedNotes || '',
    occurrences: (candidateSeed.occurrences || []).map((occurrence) => ({
      start: occurrence.start,
      endExclusive: occurrence.endExclusive,
      rawMatchedText: occurrence.rawMatchedText || '',
      sourceText: occurrence.sourceText || ''
    }))
  };
  return `known-entity-casing-${caseBuddyHashString(JSON.stringify(payload))}`;
}

export function buildKnownEntityCasingReviewKey(caseId = '', candidate = {}) {
  return [
    CASE_KNOWN_ENTITY_CASING_REVIEW_FAMILY,
    normalizeCell(caseId) || 'case',
    `row-${Number(candidate.rowIndex) + 1}`,
    normalizeCaseEntityTerm(candidate.canonicalName || ''),
    candidate.reviewBasisHash || ''
  ].join('|');
}

export function applyKnownEntityCasingReplacements(notes = '', replacements = []) {
  const source = String(notes || '');
  if (!source || !Array.isArray(replacements) || replacements.length === 0) return source;

  const sorted = replacements
    .filter(item => Number.isInteger(item.start) && Number.isInteger(item.endExclusive) && item.endExclusive > item.start && item.endExclusive <= source.length)
    .sort((left, right) => right.start - left.start || right.endExclusive - left.endExclusive);

  let output = source;
  const occupied = [];

  sorted.forEach((replacement) => {
    const overlaps = occupied.some(range => replacement.start < range.endExclusive && replacement.endExclusive > range.start);
    if (overlaps) return;
    output = `${output.slice(0, replacement.start)}${replacement.replacement}${output.slice(replacement.endExclusive)}`;
    occupied.push({ start: replacement.start, endExclusive: replacement.endExclusive });
  });

  return output;
}

export function buildKnownEntityCasingCandidatesForRow(row = [], rowIndex = 0, patterns = [], caseId = '', reviewState = {}) {
  const notes = normalizeCell(row?.[9]);
  if (!notes) return [];

  const tokens = tokenizeKnownEntityCasingNotes(notes);
  if (tokens.length === 0 || !Array.isArray(patterns) || patterns.length === 0) return [];

  const builders = new Map();

  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
    const bestPattern = patterns.find(pattern => pattern.words?.[0] === tokens[tokenIndex].normalizedWord && knownEntityCasingPatternMatchesAt(tokens, tokenIndex, pattern));
    if (!bestPattern) continue;

    const endTokenIndex = tokenIndex + bestPattern.words.length - 1;
    const start = tokens[tokenIndex].start;
    const endExclusive = tokens[endTokenIndex].endExclusive;
    const rawMatchedText = notes.slice(start, endExclusive);

    if (rawMatchedText === bestPattern.presentedName) {
      tokenIndex = endTokenIndex;
      continue;
    }

    const builderKey = `${bestPattern.kind}|${bestPattern.presentedName}`;
    if (!builders.has(builderKey)) {
      builders.set(builderKey, {
        pattern: bestPattern,
        occurrences: []
      });
    }

    builders.get(builderKey).occurrences.push({
      start,
      endExclusive,
      rawMatchedText,
      sourceText: bestPattern.name,
      replacement: bestPattern.presentedName
    });
    tokenIndex = endTokenIndex;
  }

  const decisionsByKey = reviewState?.decisionsByKey && typeof reviewState.decisionsByKey === 'object'
    ? reviewState.decisionsByKey
    : {};

  return Array.from(builders.values()).map((builder) => {
    const pattern = builder.pattern;
    const occurrences = builder.occurrences.sort((left, right) => left.start - right.start);
    const proposedNotes = applyKnownEntityCasingReplacements(notes, occurrences.map(occurrence => ({ ...occurrence, replacement: pattern.presentedName })));
    const seed = {
      rowIndex,
      hash: normalizeCell(row?.[1]),
      canonicalName: pattern.presentedName,
      kind: pattern.kind,
      currentNotes: notes,
      proposedNotes,
      occurrences
    };
    const reviewBasisHash = buildKnownEntityCasingReviewBasisHash(seed);
    const reviewKey = buildKnownEntityCasingReviewKey(caseId, { ...seed, reviewBasisHash });
    const existingDecision = decisionsByKey[reviewKey] || null;
    const hasCurrentDecision = !!existingDecision && normalizeCell(existingDecision.basisHash) === reviewBasisHash;
    const matchedForms = [...new Set(occurrences.map(occurrence => occurrence.rawMatchedText).filter(Boolean))];
    const excerpts = occurrences.map(occurrence => buildKnownEntityCasingExcerpt(notes, occurrence.start, occurrence.endExclusive));

    return {
      key: reviewKey,
      reviewKey,
      reviewBasisHash,
      rowIndex,
      rowNumber: rowIndex + 1,
      pathLabel: normalizeCell(row?.[0]),
      transactionHash: normalizeCell(row?.[1]),
      shortHash: abbreviateCaseBuddyHash(row?.[1]),
      entityKind: pattern.kind,
      entityKindLabel: pattern.kindLabel,
      canonicalName: pattern.presentedName,
      presentedName: pattern.presentedName,
      matchCount: occurrences.length,
      matchedForms,
      matchedSourceTerms: [pattern.name],
      excerpts,
      currentNotes: notes,
      proposedNotes,
      occurrences,
      hasCurrentDecision,
      currentReviewStatus: hasCurrentDecision ? normalizeKnownEntityCasingReviewStatus(existingDecision.status) : '',
      currentReviewStatusLabel: hasCurrentDecision ? formatKnownEntityCasingReviewStatus(existingDecision.status) : 'Pending',
      existingDecision,
      searchText: [
        rowIndex + 1,
        normalizeCell(row?.[0]),
        normalizeCell(row?.[1]),
        pattern.presentedName,
        pattern.kindLabel,
        notes,
        proposedNotes,
        ...matchedForms,
        ...excerpts
      ].map(value => normalizeCell(value).toLowerCase()).filter(Boolean).join(' ')
    };
  }).filter(candidate => candidate.currentNotes !== candidate.proposedNotes);
}

export function buildCaseKnownEntityCasingReviewAnalysis(mainData = [], effectiveCurators = {}, workflowState = {}, options = {}) {
  const data = normalizeCaseMatrix(mainData);
  const caseId = normalizeCell(options.caseId) || 'case';
  const reviewState = workflowState?.knownEntityCasingReview && typeof workflowState.knownEntityCasingReview === 'object'
    ? workflowState.knownEntityCasingReview
    : {};
  const effective = effectiveCurators?.all ? effectiveCurators : buildEffectiveEntityCurators({}, {});
  const patterns = buildKnownEntityCasingPatterns(effective);
  const candidates = [];
  let rowsWithNotes = 0;

  data.forEach((row, rowIndex) => {
    if (rowIndex === 0 || !rowHasMeaningfulData(row)) return;
    if (normalizeCell(row?.[9])) rowsWithNotes += 1;
    candidates.push(...buildKnownEntityCasingCandidatesForRow(row, rowIndex, patterns, caseId, reviewState));
  });

  candidates.sort((left, right) => {
    if (left.hasCurrentDecision !== right.hasCurrentDecision) return left.hasCurrentDecision ? 1 : -1;
    const rowDiff = left.rowNumber - right.rowNumber;
    if (rowDiff !== 0) return rowDiff;
    return left.presentedName.localeCompare(right.presentedName);
  });

  const pendingCandidates = candidates.filter(candidate => !candidate.hasCurrentDecision);
  const reviewedCandidates = candidates.filter(candidate => candidate.hasCurrentDecision);
  const basisPayload = candidates
    .map(candidate => ({ reviewKey: candidate.reviewKey, basisHash: candidate.reviewBasisHash, rowNumber: candidate.rowNumber }))
    .sort((left, right) => left.reviewKey.localeCompare(right.reviewKey));
  const currentBasisHash = `known-entity-casing-review-${caseBuddyHashString(JSON.stringify(basisPayload))}`;
  const completedBasisHash = normalizeCell(reviewState.completedBasisHash);
  const isCurrent = !!completedBasisHash && completedBasisHash === currentBasisHash;
  let summary = '';

  if (patterns.length === 0) {
    summary = 'No effective known-entity curator terms are loaded yet. Refresh the server curator lists or add local terms before checking note casing.';
  } else if (candidates.length === 0) {
    summary = `No known-entity note casing candidates were detected across ${rowsWithNotes} row${rowsWithNotes === 1 ? '' : 's'} with Column J notes.`;
  } else {
    summary = `${reviewedCandidates.length}/${candidates.length} known-entity note casing candidate${candidates.length === 1 ? '' : 's'} reviewed, applied, or dismissed. ${pendingCandidates.length} pending.`;
  }

  return {
    patternsLoaded: patterns.length,
    rowsWithNotes,
    totalCandidateCount: candidates.length,
    pendingCount: pendingCandidates.length,
    reviewedCount: reviewedCandidates.length,
    exchangeCandidateCount: candidates.filter(candidate => candidate.entityKind === CASE_ENTITY_KIND_EXCHANGE).length,
    nonExchangeCandidateCount: candidates.filter(candidate => candidate.entityKind === CASE_ENTITY_KIND_NON_EXCHANGE).length,
    candidates,
    pendingCandidates,
    reviewedCandidates,
    currentBasisHash,
    completedBasisHash,
    isCurrent,
    pass: pendingCandidates.length === 0,
    summary
  };
}

export function applyKnownEntityCasingFixesToMainMatrix(mainData = [], candidates = []) {
  const data = normalizeCaseMatrix(mainData);
  const selectedCandidates = (Array.isArray(candidates) ? candidates : []).filter(candidate => candidate?.reviewKey);
  const replacementsByRow = new Map();

  selectedCandidates.forEach((candidate) => {
    const rowIndex = candidate.rowIndex;
    if (!Number.isInteger(rowIndex) || rowIndex < 0) return;
    if (!replacementsByRow.has(rowIndex)) {
      replacementsByRow.set(rowIndex, {
        originalNotes: candidate.currentNotes,
        replacements: [],
        candidates: []
      });
    }
    const bucket = replacementsByRow.get(rowIndex);
    bucket.candidates.push(candidate);
    (candidate.occurrences || []).forEach((occurrence) => {
      bucket.replacements.push({
        start: occurrence.start,
        endExclusive: occurrence.endExclusive,
        replacement: candidate.presentedName || candidate.canonicalName || '',
        candidateKey: candidate.reviewKey
      });
    });
  });

  let rowsUpdated = 0;
  let fixCount = 0;
  let skippedCount = 0;
  const updatedRows = [];

  replacementsByRow.forEach((bucket, rowIndex) => {
    const row = data[rowIndex];
    if (!row) return;
    const currentNotes = normalizeCell(row?.[9]);
    if (currentNotes !== normalizeCell(bucket.originalNotes)) {
      skippedCount += bucket.candidates.length;
      return;
    }
    const proposedNotes = applyKnownEntityCasingReplacements(currentNotes, bucket.replacements);
    if (proposedNotes === currentNotes) {
      skippedCount += bucket.candidates.length;
      return;
    }
    row[9] = proposedNotes;
    rowsUpdated += 1;
    fixCount += bucket.candidates.length;
    updatedRows.push({ rowIndex, rowNumber: rowIndex + 1, currentNotes, proposedNotes, candidates: bucket.candidates });
  });

  return {
    data,
    changed: rowsUpdated > 0,
    rowsUpdated,
    fixCount,
    skippedCount,
    updatedRows
  };
}

export function buildKnownEntityCasingAuditRows(analysis = {}, actionLabel = '') {
  const rows = [
    ['Case Buddy Known Entity Note Casing Audit', '', '', '', '', '', '', '', '', 'Generated by Review known-entity note casing. Audit only; Main Data Column J remains the source of truth.'],
    ['Action', actionLabel || 'Review refreshed', 'Patterns Loaded', analysis.patternsLoaded || 0, 'Rows With Notes', analysis.rowsWithNotes || 0, 'Candidates', analysis.totalCandidateCount || 0, 'Pending', analysis.pendingCount || 0],
    ['Status', analysis.summary || '', 'Basis', analysis.currentBasisHash || '', 'Completed Basis', analysis.completedBasisHash || '', 'Exchange Candidates', analysis.exchangeCandidateCount || 0, 'Non-exchange Candidates', analysis.nonExchangeCandidateCount || 0],
    ['Review Status', 'Row', 'Path Label', 'Hash', 'Kind', 'Expected Name', 'Matched Forms', 'Current Notes', 'Proposed Notes']
  ];

  (analysis.candidates || []).forEach((candidate) => {
    rows.push([
      candidate.hasCurrentDecision ? candidate.currentReviewStatusLabel : 'Pending',
      candidate.rowNumber || '',
      candidate.pathLabel || '',
      candidate.transactionHash || '',
      candidate.entityKindLabel || '',
      candidate.presentedName || '',
      (candidate.matchedForms || []).join(', '),
      candidate.currentNotes || '',
      candidate.proposedNotes || ''
    ]);
  });

  return rows;
}
